import React, { useCallback, useEffect, useMemo, useState } from 'react';

type ImportMetaEnvLike = {
  env: {
    VITE_FEEDBACK_API_URL?: string;
    VITE_FEEDBACK_SITE_URL?: string;
    VITE_FEEDBACK_SOCIAL_IMAGE_URL?: string;
    VITE_FEEDBACK_LOGIN_URL?: string;
    VITE_FEEDBACK_ENABLE_SIGNUP?: string;
    VITE_FEEDBACK_DOCS_URL?: string;
  };
};

const env = ((import.meta as unknown) as ImportMetaEnvLike).env;
const apiBase = env.VITE_FEEDBACK_API_URL ?? '/api/feedback/v1';
const fallbackSiteUrl = typeof window === 'undefined' ? '' : window.location.origin;
const siteBase = env.VITE_FEEDBACK_SITE_URL ?? (fallbackSiteUrl || 'https://feedback.digiwedge.com');
const socialImageUrl = env.VITE_FEEDBACK_SOCIAL_IMAGE_URL ?? `${siteBase}/feedback-social.svg`;
const loginUrl = env.VITE_FEEDBACK_LOGIN_URL;
const enableSignup = env.VITE_FEEDBACK_ENABLE_SIGNUP === 'true';

/** Derive docs URL from hostname: feedback.X → docs.feedback.X */
const getDocsUrl = (): string => {
  if (env.VITE_FEEDBACK_DOCS_URL) return env.VITE_FEEDBACK_DOCS_URL;
  if (typeof window === 'undefined') return 'https://docs.feedback.digiwedge.com';
  const host = window.location.hostname;
  // feedback.yolo.co.za → docs.feedback.yolo.co.za
  // feedback.digiwedge.com → docs.feedback.digiwedge.com
  // feedback.uat.digiwedge.com → docs.feedback.uat.digiwedge.com
  return `https://docs.${host}`;
};
const docsUrl = getDocsUrl();

type DarkModeConfig = {
  ink?: string;
  inkMuted?: string;
  surface?: string;
  surfaceStrong?: string;
  accent?: string;
  accentDeep?: string;
  accentSoft?: string;
};

type BrandConfig = {
  accent?: string;
  accentDeep?: string;
  accentSoft?: string;
  heroGlow?: [string, string, string];
  heroTitle?: string;
  heroSubtitle?: string;
  heroBadge?: string;
  logoUrl?: string;
  darkMode?: DarkModeConfig;
};

type TenantConfig = {
  id: string;
  slug: string;
  name: string;
  domain?: string | null;
  plan: string;
  status: string;
  settings?: Record<string, unknown> | null;
  brandConfig?: BrandConfig | null;
  products?: string[];
};

type Feature = {
  id: string;
  title: string;
  description: string;
  problem?: string | null;
  status: string;
  horizon: string;
  product?: string | null;
  category?: string | null;
  totalCredits: number;
  uniqueVoters: number;
  updatedAt?: string | null;
  releaseNotesUrl?: string | null;
  shippedVersion?: string | null;
};

type RoadmapResponse = {
  NOW: Feature[];
  NEXT: Feature[];
  LATER: Feature[];
};

type CreditsResponse = {
  total: number;
  allocated: number;
  available: number;
  quarterKey: string;
};

type VoteRecord = {
  featureId: string;
  credits: number;
  feature: {
    id: string;
    title: string;
    status: string;
    horizon: string;
    totalCredits: number;
  };
};

type CreditHistoryEntry = {
  id: string;
  featureId?: string | null;
  type: string;
  creditsChange: number;
  balanceAfter: number;
  reason?: string | null;
  createdAt: string;
};

type HealthResponse = {
  status: string;
  info?: { database?: { status?: string } };
};

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(text || `Request failed (${res.status})`);
  }
  if (!text) {
    return {} as T;
  }
  return JSON.parse(text) as T;
}

const horizonMeta = [
  { key: 'NOW', label: 'Now', detail: 'This quarter focus' },
  { key: 'NEXT', label: 'Next', detail: 'Queued for next cycle' },
  { key: 'LATER', label: 'Later', detail: 'Under investigation' },
] as const;

const statusOptions = [
  'ALL',
  'INVESTIGATING',
  'PLANNED',
  'IN_PROGRESS',
  'BETA',
  'SHIPPED',
  'CLOSED',
] as const;

type StatusFilter = (typeof statusOptions)[number];

const getFeatureFromQuery = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('feature');
};

const formatStatus = (status: string) =>
  status
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatDate = (value?: string | null) => {
  if (!value) return 'Recently shipped';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently shipped';
  return new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium' }).format(date);
};

const DEFAULT_DARK_MODE: DarkModeConfig = {
  ink: '#f8fafc',
  inkMuted: '#b4bccb',
  surface: '#0f1115',
  surfaceStrong: '#1a1d24',
  accent: '#5a8fff',
  accentDeep: '#7aa3ff',
  accentSoft: '#0f1a2e',
};

const DEFAULT_BRAND: Required<BrandConfig> = {
  accent: '#2f6fff',
  accentDeep: '#1c3f9b',
  accentSoft: '#e8f0ff',
  heroGlow: ['#eef4ff', '#f3f6fb', '#f4f1ea'],
  heroTitle: 'Shape the product roadmap with your real priorities.',
  heroSubtitle:
    'Use 10 quarterly credits to vote on the improvements that matter most. Reallocate anytime and reclaim credits once a feature ships.',
  heroBadge: 'Customer Council',
  logoUrl: '',
  darkMode: DEFAULT_DARK_MODE,
};

const resolveBrandConfig = (tenant: TenantConfig | null): Required<BrandConfig> => {
  const name = tenant?.name ?? 'Product';
  const brand = tenant?.brandConfig ?? {};
  return {
    accent: brand.accent ?? DEFAULT_BRAND.accent,
    accentDeep: brand.accentDeep ?? DEFAULT_BRAND.accentDeep,
    accentSoft: brand.accentSoft ?? DEFAULT_BRAND.accentSoft,
    heroGlow: brand.heroGlow ?? DEFAULT_BRAND.heroGlow,
    heroTitle: brand.heroTitle ?? `Shape the ${name} roadmap with your real priorities.`,
    heroSubtitle: brand.heroSubtitle ?? DEFAULT_BRAND.heroSubtitle,
    heroBadge: brand.heroBadge ?? `${name} Customer Council`,
    logoUrl: brand.logoUrl ?? DEFAULT_BRAND.logoUrl,
    darkMode: brand.darkMode ?? DEFAULT_BRAND.darkMode,
  };
};

const applyBrandConfig = (brand: Required<BrandConfig>, isDark: boolean) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const dark = brand.darkMode;

  if (isDark && dark) {
    root.style.setProperty('--ink', dark.ink ?? '#f8fafc');
    root.style.setProperty('--ink-muted', dark.inkMuted ?? '#b4bccb');
    root.style.setProperty('--surface', dark.surface ?? '#0f1115');
    root.style.setProperty('--surface-strong', dark.surfaceStrong ?? '#1a1d24');
    root.style.setProperty('--accent', dark.accent ?? brand.accent);
    root.style.setProperty('--accent-deep', dark.accentDeep ?? brand.accentDeep);
    root.style.setProperty('--accent-soft', dark.accentSoft ?? '#1a1a2e');
    root.style.setProperty('--border', 'rgba(255, 255, 255, 0.1)');
    root.style.setProperty('--hero-glow-1', dark.surface ?? '#0f1115');
    root.style.setProperty('--hero-glow-2', dark.surfaceStrong ?? '#1a1d24');
    root.style.setProperty('--hero-glow-3', dark.surface ?? '#0f1115');
    root.setAttribute('data-theme', 'dark');
  } else {
    root.style.setProperty('--ink', '#0f1115');
    root.style.setProperty('--ink-muted', '#5d6470');
    root.style.setProperty('--surface', '#f3f1ec');
    root.style.setProperty('--surface-strong', '#ffffff');
    root.style.setProperty('--accent', brand.accent);
    root.style.setProperty('--accent-deep', brand.accentDeep);
    root.style.setProperty('--accent-soft', brand.accentSoft);
    root.style.setProperty('--border', 'rgba(15, 17, 21, 0.08)');
    root.style.setProperty('--hero-glow-1', brand.heroGlow[0]);
    root.style.setProperty('--hero-glow-2', brand.heroGlow[1]);
    root.style.setProperty('--hero-glow-3', brand.heroGlow[2]);
    root.removeAttribute('data-theme');
  }
};

const setMetaTag = (name: string, content: string) => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setMetaProperty = (property: string, content: string) => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setLinkTag = (key: string, attrs: Record<string, string>) => {
  if (typeof document === 'undefined') return;
  let link = document.querySelector(`link[data-feed="${key}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('data-feed', key);
    document.head.appendChild(link);
  }
  Object.entries(attrs).forEach(([attr, value]) => link?.setAttribute(attr, value));
};

export function App() {
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [roadmap, setRoadmap] = useState<RoadmapResponse | null>(null);
  const [shipped, setShipped] = useState<Feature[]>([]);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(() => localStorage.getItem('feedbackToken') ?? '');
  const [credits, setCredits] = useState<CreditsResponse | null>(null);
  const [votes, setVotes] = useState<VoteRecord[]>([]);
  const [creditHistory, setCreditHistory] = useState<CreditHistoryEntry[]>([]);
  const [voteStatus, setVoteStatus] = useState<string | null>(null);
  const [privateError, setPrivateError] = useState<string | null>(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(() => getFeatureFromQuery());
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [productFilter, setProductFilter] = useState('ALL');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestCode, setGuestCode] = useState('');
  const [guestWebsite, setGuestWebsite] = useState('');
  const [guestVerified, setGuestVerified] = useState(false);
  const [guestStatus, setGuestStatus] = useState<string | null>(null);
  const [shipReleaseNotesUrl, setShipReleaseNotesUrl] = useState('');
  const [shipVersion, setShipVersion] = useState('');
  const [adminStatus, setAdminStatus] = useState<string | null>(null);
  const [signupName, setSignupName] = useState('');
  const [signupSlug, setSignupSlug] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupDomain, setSignupDomain] = useState('');
  const [signupStatus, setSignupStatus] = useState<string | null>(null);
  const [themePreference, setThemePreference] = useState<'light' | 'dark' | 'system'>(() => {
    if (typeof window === 'undefined') return 'system';
    const stored = localStorage.getItem('feedbackTheme');
    if (stored === 'light' || stored === 'dark') return stored;
    return 'system';
  });
  const [systemDark, setSystemDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const darkMode = themePreference === 'system' ? systemDark : themePreference === 'dark';

  const feedJsonUrl = useMemo(() => `${apiBase}/features/feed.json`, []);
  const feedRssUrl = useMemo(() => `${apiBase}/features/feed.xml`, []);
  const brand = useMemo(() => resolveBrandConfig(tenantConfig), [tenantConfig]);
  const allowedProducts = useMemo(() => {
    const products = tenantConfig?.products ?? [];
    return products.length ? new Set(products) : null;
  }, [tenantConfig]);

  const loadTenantConfig = useCallback(async () => {
    setTenantLoading(true);
    setTenantError(null);
    try {
      const config = await fetchJson<TenantConfig>('/tenant/config');
      setTenantConfig(config);
    } catch (err) {
      setTenantConfig(null);
      setTenantError((err as Error).message);
    } finally {
      setTenantLoading(false);
    }
  }, []);

  const allFeatures = useMemo(() => {
    const combined = [
      ...(roadmap?.NOW ?? []),
      ...(roadmap?.NEXT ?? []),
      ...(roadmap?.LATER ?? []),
      ...shipped,
    ];
    if (!allowedProducts) return combined;
    return combined.filter((feature) => !feature.product || allowedProducts.has(feature.product));
  }, [allowedProducts, roadmap, shipped]);

  const featureIndex = useMemo(() => {
    return new Map(allFeatures.map((feature) => [feature.id, feature]));
  }, [allFeatures]);

  const selectedFeature = selectedFeatureId ? featureIndex.get(selectedFeatureId) ?? null : null;

  const productOptions = useMemo(() => {
    const products = new Set<string>();
    allFeatures.forEach((feature) => {
      if (feature.product) {
        products.add(feature.product);
      }
    });
    if (allowedProducts) {
      return Array.from(products).filter((product) => allowedProducts.has(product)).sort();
    }
    return Array.from(products).sort();
  }, [allFeatures, allowedProducts]);

  useEffect(() => {
    if (productFilter !== 'ALL' && !productOptions.includes(productFilter)) {
      setProductFilter('ALL');
    }
  }, [productFilter, productOptions]);

  const filteredRoadmap = useMemo(() => {
    const applyFilters = (features: Feature[]) =>
      features.filter((feature) => {
        if (allowedProducts && feature.product && !allowedProducts.has(feature.product)) return false;
        if (statusFilter !== 'ALL' && feature.status !== statusFilter) return false;
        if (productFilter !== 'ALL' && feature.product !== productFilter) return false;
        if (search) {
          const haystack = `${feature.title} ${feature.description} ${feature.category ?? ''}`.toLowerCase();
          if (!haystack.includes(search.toLowerCase())) return false;
        }
        return true;
      });

    return {
      NOW: applyFilters(roadmap?.NOW ?? []),
      NEXT: applyFilters(roadmap?.NEXT ?? []),
      LATER: applyFilters(roadmap?.LATER ?? []),
    };
  }, [roadmap, productFilter, search, statusFilter, allowedProducts]);

  const filteredShipped = useMemo(() => {
    return shipped.filter((feature) => {
      if (allowedProducts && feature.product && !allowedProducts.has(feature.product)) return false;
      if (productFilter !== 'ALL' && feature.product !== productFilter) return false;
      if (search) {
        const haystack = `${feature.title} ${feature.description} ${feature.category ?? ''}`.toLowerCase();
        if (!haystack.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [shipped, productFilter, search, allowedProducts]);

  const relatedFeatures = useMemo(() => {
    if (!selectedFeature) return [];
    return allFeatures
      .filter((feature) => feature.id !== selectedFeature.id)
      .filter((feature) => {
        if (selectedFeature.product && feature.product === selectedFeature.product) return true;
        if (selectedFeature.category && feature.category === selectedFeature.category) return true;
        return false;
      })
      .slice(0, 3);
  }, [allFeatures, selectedFeature]);

  const loadPublic = useCallback(async () => {
    if (!tenantConfig) return;
    setLoading(true);
    setError(null);
    try {
      const [healthRes, roadmapRes, shippedRes] = await Promise.all([
        fetchJson<HealthResponse>('/health'),
        fetchJson<RoadmapResponse>(`/features/roadmap`),
        fetchJson<Feature[]>(`/features/shipped?limit=30`),
      ]);
      setHealth(healthRes);
      setRoadmap(roadmapRes);
      setShipped(shippedRes);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [tenantConfig]);

  const loadPrivate = useCallback(async () => {
    if (!tenantConfig) return;
    if (!token) {
      setCredits(null);
      setVotes([]);
      setCreditHistory([]);
      setPrivateError(null);
      return;
    }

    try {
      const [creditRes, votesRes, historyRes] = await Promise.all([
        fetchJson<CreditsResponse>(`/credits/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetchJson<VoteRecord[]>(`/votes/my`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetchJson<CreditHistoryEntry[]>(`/credits/history?limit=6`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setCredits(creditRes);
      setVotes(votesRes);
      setCreditHistory(historyRes);
      setPrivateError(null);
    } catch (err) {
      setCredits(null);
      setVotes([]);
      setCreditHistory([]);
      setPrivateError((err as Error).message);
    }
  }, [tenantConfig, token]);

  useEffect(() => {
    void loadTenantConfig();
  }, [loadTenantConfig]);

  useEffect(() => {
    if (!tenantConfig) return;
    void loadPublic();
  }, [loadPublic, tenantConfig]);

  useEffect(() => {
    if (!tenantConfig) return;
    void loadPrivate();
  }, [loadPrivate, tenantConfig]);

  useEffect(() => {
    applyBrandConfig(brand, darkMode);
  }, [brand, darkMode]);

  const handleThemeChange = useCallback((value: 'light' | 'dark' | 'system') => {
    setThemePreference(value);
    if (value === 'system') {
      localStorage.removeItem('feedbackTheme');
    } else {
      localStorage.setItem('feedbackTheme', value);
    }
  }, []);

  // Listen for system preference changes (with Safari fallback)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setSystemDark(e.matches);
    // Modern browsers use addEventListener, older Safari uses addListener
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else if (mq.addListener) {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }
  }, []);

  useEffect(() => {
    setGuestVerified(false);
    setGuestStatus(null);
    setGuestCode('');
  }, [tenantConfig?.slug]);

  useEffect(() => {
    if (guestVerified) {
      setGuestVerified(false);
      setGuestStatus(null);
      setGuestCode('');
    }
  }, [guestEmail, guestVerified]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const metaTitle = selectedFeature
      ? `${selectedFeature.title} · ${tenantConfig?.name ?? 'Product'} Roadmap`
      : `${tenantConfig?.name ?? 'Product'} Product Feedback Portal`;
    const metaDescription = selectedFeature?.description ?? brand.heroSubtitle;
    const basePath = typeof window === 'undefined' ? '' : window.location.origin + window.location.pathname;
    const url = selectedFeature ? `${basePath}?feature=${selectedFeature.id}` : `${basePath}`;

    document.title = metaTitle;
    setMetaTag('description', metaDescription);
    setMetaProperty('og:title', metaTitle);
    setMetaProperty('og:description', metaDescription);
    setMetaProperty('og:image', socialImageUrl);
    setMetaProperty('og:url', url);
    setMetaProperty('og:type', 'website');
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', metaTitle);
    setMetaTag('twitter:description', metaDescription);
    setMetaTag('twitter:image', socialImageUrl);
    setLinkTag('json', {
      rel: 'alternate',
      type: 'application/feed+json',
      title: `${tenantConfig?.name ?? 'Product'} shipped updates (JSON)`,
      href: feedJsonUrl,
    });
    setLinkTag('rss', {
      rel: 'alternate',
      type: 'application/rss+xml',
      title: `${tenantConfig?.name ?? 'Product'} shipped updates (RSS)`,
      href: feedRssUrl,
    });
  }, [brand.heroSubtitle, feedJsonUrl, feedRssUrl, selectedFeature, tenantConfig?.name]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (selectedFeatureId) {
      url.searchParams.set('feature', selectedFeatureId);
    } else {
      url.searchParams.delete('feature');
    }
    window.history.replaceState({}, '', url);
  }, [selectedFeatureId]);

  useEffect(() => {
    if (!selectedFeatureId || selectedFeature) return;
    if (!loading) {
      setSelectedFeatureId(null);
    }
  }, [loading, selectedFeature, selectedFeatureId]);

  useEffect(() => {
    if (!selectedFeature) {
      setShipReleaseNotesUrl('');
      setShipVersion('');
      setAdminStatus(null);
      return;
    }
    setShipReleaseNotesUrl(selectedFeature.releaseNotesUrl ?? '');
    setShipVersion(selectedFeature.shippedVersion ?? '');
    setAdminStatus(null);
  }, [selectedFeature]);

  const saveToken = (value: string) => {
    const trimmed = value.trim();
    setToken(trimmed);
    if (trimmed) {
      localStorage.setItem('feedbackToken', trimmed);
    } else {
      localStorage.removeItem('feedbackToken');
    }
    setVoteStatus(null);
  };

  const openFeature = (featureId: string) => {
    setSelectedFeatureId(featureId);
    setShareStatus(null);
  };

  const closeFeature = () => {
    setSelectedFeatureId(null);
    setShareStatus(null);
  };

  const shareLink = selectedFeature
    ? `${typeof window === 'undefined' ? '' : window.location.origin + window.location.pathname}?feature=${selectedFeature.id}`
    : '';

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setShareStatus('Link copied.');
      setTimeout(() => setShareStatus(null), 2000);
    } catch (err) {
      setShareStatus((err as Error).message);
    }
  };

  const submitSignup = async () => {
    if (!enableSignup) return;
    if (!signupName || !signupSlug || !signupEmail) {
      setSignupStatus('Name, slug, and email are required.');
      return;
    }
    setSignupStatus(null);
    try {
      const res = await fetchJson<{ status: string; message?: string; tenantSlug?: string }>(`/tenant/signup`, {
        method: 'POST',
        body: JSON.stringify({
          name: signupName.trim(),
          slug: signupSlug.trim().toLowerCase(),
          email: signupEmail.trim().toLowerCase(),
          domain: signupDomain.trim() ? signupDomain.trim().toLowerCase() : undefined,
        }),
      });
      if (res.status === 'created') {
        setSignupStatus('Tenant created. Check your email for next steps.');
      } else {
        setSignupStatus(res.message ?? 'Tenant already exists.');
      }
    } catch (err) {
      setSignupStatus((err as Error).message);
    }
  };

  const registerGuest = async () => {
    if (!guestEmail) {
      setGuestStatus('Enter an email to receive a verification code.');
      return;
    }
    setGuestStatus(null);
    try {
      const res = await fetchJson<{ message: string; verificationCode?: string }>(`/guests/register`, {
        method: 'POST',
        body: JSON.stringify({ email: guestEmail, website: guestWebsite }),
      });
      const devHint = res.verificationCode ? ` Code: ${res.verificationCode}` : '';
      setGuestStatus(`${res.message}${devHint}`);
    } catch (err) {
      setGuestStatus((err as Error).message);
    }
  };

  const verifyGuest = async () => {
    if (!guestEmail || !guestCode) {
      setGuestStatus('Enter the email and 6-digit code to verify.');
      return;
    }
    setGuestStatus(null);
    try {
      const res = await fetchJson<{ message: string }>(`/guests/verify`, {
        method: 'POST',
        body: JSON.stringify({ email: guestEmail, code: guestCode }),
      });
      setGuestVerified(true);
      setGuestStatus(res.message);
    } catch (err) {
      setGuestStatus((err as Error).message);
    }
  };

  const castVote = async (featureId: string, creditsToSpend: number) => {
    if (!token && !guestVerified) {
      setVoteStatus('Sign in or verify as guest to vote.');
      return;
    }
    if (!token && guestVerified && !guestEmail) {
      setVoteStatus('Enter your guest email to vote.');
      return;
    }
    setVoteStatus(null);
    try {
      if (token) {
        await fetchJson(`/votes`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: JSON.stringify({ featureId, credits: creditsToSpend }),
        });
        await Promise.all([loadPublic(), loadPrivate()]);
      } else {
        await fetchJson(`/guests/vote`, {
          method: 'POST',
          body: JSON.stringify({ featureId, credits: creditsToSpend, email: guestEmail }),
        });
        await loadPublic();
      }
      setVoteStatus(`Vote recorded: +${creditsToSpend}`);
    } catch (err) {
      setVoteStatus((err as Error).message);
    }
  };

  const removeVote = async (featureId: string) => {
    if (!token) return;
    setVoteStatus(null);
    try {
      await fetchJson(`/votes/${featureId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await Promise.all([loadPublic(), loadPrivate()]);
      setVoteStatus('Vote removed and credits returned.');
    } catch (err) {
      setVoteStatus((err as Error).message);
    }
  };

  const shipFeature = async () => {
    if (!selectedFeature) return;
    if (!token) {
      setAdminStatus('Sign in with an admin token to ship features.');
      return;
    }
    setAdminStatus(null);
    const payload: { releaseNotesUrl?: string; shippedVersion?: string } = {};
    if (shipReleaseNotesUrl.trim()) payload.releaseNotesUrl = shipReleaseNotesUrl.trim();
    if (shipVersion.trim()) payload.shippedVersion = shipVersion.trim();
    try {
      await fetchJson(`/admin/features/${selectedFeature.id}/ship`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      await Promise.all([loadPublic(), loadPrivate()]);
      setAdminStatus('Feature marked as shipped.');
    } catch (err) {
      setAdminStatus((err as Error).message);
    }
  };

  const availablePercent = credits ? Math.max(0, Math.min(100, (credits.available / credits.total) * 100)) : 0;

  if (tenantLoading) {
    return (
      <div className="page">
        <div className="loading">Loading tenant…</div>
      </div>
    );
  }

  if (tenantError || !tenantConfig) {
    return (
      <div className="page">
        <div className="error">
          Unable to resolve tenant configuration. Please check your domain setup.
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="hero">
        <div>
          <div className="brand-mark">
            <span>{tenantConfig?.name ?? 'Product'}</span>
            <span className="brand-dot" />
            <select
              className="theme-select"
              value={themePreference}
              onChange={(e) => handleThemeChange(e.target.value as 'light' | 'dark' | 'system')}
              title="Theme preference"
            >
              <option value="system">System</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
            <a
              className="help-link"
              href={docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Help & Documentation"
            >
              ?
            </a>
          </div>
          <p className="eyebrow">{brand.heroBadge}</p>
          <h1>{brand.heroTitle}</h1>
          <p className="hero-subtitle">{brand.heroSubtitle}</p>
          <div className="hero-actions">
            <button
              className="btn btn-primary"
              onClick={() => document.getElementById('roadmap')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Roadmap
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => document.getElementById('vote')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Manage Voting
            </button>
          </div>
        </div>
        <div className="hero-panel">
          <div className="tenant-locked">
            <span className="label">Tenant</span>
            <strong>{tenantConfig?.name ?? 'Product'}</strong>
          </div>
          <div className="health-card">
            <div>
              <p className="label">API Status</p>
              <p className={health?.status === 'ok' ? 'status ok' : 'status warn'}>
                {health?.status === 'ok' ? 'Operational' : 'Unavailable'}
              </p>
            </div>
            <div>
              <p className="label">Database</p>
              <p className={health?.info?.database?.status === 'up' ? 'status ok' : 'status warn'}>
                {health?.info?.database?.status ?? 'unknown'}
              </p>
            </div>
          </div>
          <div className="access-card" id="vote">
            <div className="access-header">
              <div>
                <p className="label">Voting Access</p>
                <p className="meta">Sign in or verify as a guest to allocate credits.</p>
              </div>
              {loginUrl && (
                <a className="btn btn-primary" href={loginUrl} rel="noreferrer">
                  Sign In
                </a>
              )}
            </div>
            <label className="token-input">
              <span>JWT Token (advanced)</span>
              <textarea
                placeholder="Paste your access token to unlock credits and voting history"
                value={token}
                onChange={(event) => saveToken(event.target.value)}
                rows={3}
              />
            </label>
            {credits ? (
              <div className="credit-meter">
                <div className="credit-row">
                  <p className="credits">{credits.available} credits available</p>
                  <span className="meta">{credits.allocated} allocated</span>
                </div>
                <div className="meter">
                  <span style={{ width: `${availablePercent}%` }} />
                </div>
                <p className="meta">Quarter {credits.quarterKey}</p>
              </div>
            ) : (
              <p className="meta">No active wallet loaded yet.</p>
            )}
            {privateError && <p className="status-note">{privateError}</p>}
            {voteStatus && <p className="status-note">{voteStatus}</p>}
          </div>
          {enableSignup && (
            <div className="signup-card" id="signup">
              <div className="access-header">
                <div>
                  <p className="label">Start a trial</p>
                  <p className="meta">Create your own roadmap workspace in minutes.</p>
                </div>
              </div>
              <div className="signup-grid">
                <label className="token-input">
                  <span>Workspace name</span>
                  <input
                    type="text"
                    placeholder="Acme Corp"
                    value={signupName}
                    onChange={(event) => setSignupName(event.target.value)}
                  />
                </label>
                <label className="token-input">
                  <span>Workspace slug</span>
                  <input
                    type="text"
                    placeholder="acme"
                    value={signupSlug}
                    onChange={(event) => setSignupSlug(event.target.value)}
                  />
                </label>
                <label className="token-input">
                  <span>Contact email</span>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={signupEmail}
                    onChange={(event) => setSignupEmail(event.target.value)}
                  />
                </label>
                <label className="token-input">
                  <span>Custom domain (optional)</span>
                  <input
                    type="text"
                    placeholder="roadmap.company.com"
                    value={signupDomain}
                    onChange={(event) => setSignupDomain(event.target.value)}
                  />
                </label>
              </div>
              <button className="btn btn-primary" onClick={submitSignup}>
                Create workspace
              </button>
              {signupStatus && <p className="status-note">{signupStatus}</p>}
            </div>
          )}
        </div>
      </header>

      <section className="steps">
        <div className="step">
          <span>01</span>
          <h3>Allocate 1 to 3 credits</h3>
          <p>Back the items that will move the needle. Spread or stack your credits.</p>
        </div>
        <div className="step">
          <span>02</span>
          <h3>Rebalance anytime</h3>
          <p>Shift credits to new priorities without losing momentum.</p>
        </div>
        <div className="step">
          <span>03</span>
          <h3>Credits return on ship</h3>
          <p>Shipped features automatically free up your budget.</p>
        </div>
      </section>

      <section className="voting-hub">
        <div className="hub-card">
          <h3>Guest voting</h3>
          <p className="meta">
            Verify your email to vote without signing in. Guest voting is rate-limited and monitored for abuse.
          </p>
          <label className="trap-field" aria-hidden="true">
            <span>Website</span>
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={guestWebsite}
              onChange={(event) => setGuestWebsite(event.target.value)}
            />
          </label>
          <label className="token-input">
            <span>Email</span>
            <input
              type="email"
              placeholder="name@company.com"
              value={guestEmail}
              onChange={(event) => setGuestEmail(event.target.value)}
            />
          </label>
          <div className="hub-actions">
            <button className="btn btn-ghost" onClick={registerGuest}>
              Send code
            </button>
            <button className="btn btn-ghost" onClick={verifyGuest}>
              Verify
            </button>
          </div>
          <label className="token-input">
            <span>Verification code</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              placeholder="6-digit code"
              value={guestCode}
              onChange={(event) => setGuestCode(event.target.value)}
            />
          </label>
          <p className={guestVerified ? 'status ok' : 'status warn'}>
            {guestVerified ? 'Email verified for guest voting.' : 'Not verified yet.'}
          </p>
          {guestStatus && <p className="status-note">{guestStatus}</p>}
        </div>

        <div className="hub-card">
          <h3>Your active votes</h3>
          {token ? (
            <>
              <div className="history-list">
                {votes.map((vote) => (
                  <div key={vote.featureId} className="history-item">
                    <div>
                      <p className="history-title">{vote.feature.title}</p>
                      <p className="meta">{formatStatus(vote.feature.status)}</p>
                    </div>
                    <div className="history-actions">
                      <span className="credits">+{vote.credits}</span>
                      <button className="btn btn-mini" onClick={() => removeVote(vote.featureId)}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {!votes.length && <div className="empty">No active votes yet.</div>}
              </div>
              <div className="history-list small">
                <p className="label">Recent credit activity</p>
                {creditHistory.map((entry) => (
                  <div key={entry.id} className="history-item compact">
                    <span className="meta">{formatDate(entry.createdAt)}</span>
                    <span className={entry.creditsChange > 0 ? 'credits positive' : 'credits negative'}>
                      {entry.creditsChange > 0 ? '+' : ''}
                      {entry.creditsChange}
                    </span>
                    <span className="meta">Balance {entry.balanceAfter}</span>
                  </div>
                ))}
                {!creditHistory.length && <div className="empty">No transactions yet.</div>}
              </div>
            </>
          ) : (
            <div className="empty">Sign in to see your vote history.</div>
          )}
        </div>
      </section>

      <section className="roadmap" id="roadmap">
        <div className="section-header">
          <div>
            <h2>Public Roadmap</h2>
            <p>Ranked by the community. Highest credit totals rise to the top.</p>
          </div>
          <div className="filters">
            <label>
              <span className="label">Search</span>
              <input
                className="filter-input"
                placeholder="Search features"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <label>
              <span className="label">Status</span>
              <select
                className="filter-input"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status === 'ALL' ? 'All statuses' : formatStatus(status)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="label">Product</span>
              <select
                className="filter-input"
                value={productFilter}
                onChange={(event) => setProductFilter(event.target.value)}
              >
                <option value="ALL">All products</option>
                {productOptions.map((product) => (
                  <option key={product} value={product}>
                    {product}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn btn-mini"
              onClick={() => {
                setSearch('');
                setStatusFilter('ALL');
                setProductFilter('ALL');
              }}
            >
              Clear
            </button>
          </div>
        </div>
        <div className="guidance">
          <div>
            <h4>Voting guidance</h4>
            <p>
              Credits are capped at 10 per user per quarter. Votes can be rebalanced at any time, and credits return
              automatically when a feature ships.
            </p>
          </div>
          {credits ? (
            <div className="guidance-meter">
              <p className="label">Credits remaining</p>
              <p className="credits">{credits.available} / {credits.total}</p>
            </div>
          ) : (
            <p className="meta">Sign in or verify as guest to activate voting.</p>
          )}
        </div>
        {loading && <p className="meta">Loading roadmap…</p>}
        {error && (
          <div className="error">
            {error}
            <button className="btn btn-mini" onClick={loadPublic}>
              Retry
            </button>
          </div>
        )}
        <div className="columns">
          {horizonMeta.map((horizon) => (
            <div key={horizon.key} className="column">
              <div className="column-header">
                <div>
                  <h3>{horizon.label}</h3>
                  <p>{horizon.detail}</p>
                </div>
                <span className="pill">{filteredRoadmap[horizon.key].length}</span>
              </div>
              <div className="cards">
                {filteredRoadmap[horizon.key].map((feature) => (
                  <article key={feature.id} className="card">
                    <header>
                      <div>
                        <h4>{feature.title}</h4>
                        <div className="tag-row">
                          {feature.product && <span className="tag">{feature.product}</span>}
                          {feature.category && <span className="tag">{feature.category}</span>}
                        </div>
                      </div>
                      <span className="badge" data-status={feature.status}>
                        {formatStatus(feature.status)}
                      </span>
                    </header>
                    <p>{feature.description}</p>
                    <div className="card-meta">
                      <span>{feature.totalCredits} credits</span>
                      <span>{feature.uniqueVoters} voters</span>
                    </div>
                    <div className="card-actions">
                      <button className="btn btn-mini" onClick={() => openFeature(feature.id)}>
                        Details
                      </button>
                      <div className="vote-actions">
                        {[1, 2, 3].map((amount) => (
                          <button
                            key={amount}
                            className="btn btn-mini"
                            disabled={!token && !guestVerified}
                            onClick={() => castVote(feature.id, amount)}
                            title={!token && !guestVerified ? 'Sign in or verify email to vote' : `Add ${amount} credit`}
                          >
                            +{amount}
                          </button>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
                {!filteredRoadmap[horizon.key].length && !loading && (
                  <div className="empty">No items match the filters yet.</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="shipped">
        <div className="section-header">
          <div>
            <h2>Recently Shipped</h2>
            <p>Fresh releases driven by real demand. Subscribe for weekly updates.</p>
          </div>
          <div className="subscribe">
            <a className="btn btn-ghost" href={feedJsonUrl} target="_blank" rel="noreferrer">
              JSON feed
            </a>
            <a className="btn btn-ghost" href={feedRssUrl} target="_blank" rel="noreferrer">
              RSS feed
            </a>
          </div>
        </div>
        <div className="shipped-grid">
          {filteredShipped.map((feature) => (
            <div key={feature.id} className="shipped-card">
              <span className="ship-date">{formatDate(feature.updatedAt)}</span>
              <h4>{feature.title}</h4>
              {feature.product && <span className="tag shipped-tag">{feature.product}</span>}
              <p>{feature.description}</p>
              <div className="shipped-actions">
                {feature.releaseNotesUrl && (
                  <a className="btn btn-mini" href={feature.releaseNotesUrl} target="_blank" rel="noreferrer">
                    Release notes
                  </a>
                )}
                <button className="btn btn-mini" onClick={() => openFeature(feature.id)}>
                  View details
                </button>
              </div>
            </div>
          ))}
          {!filteredShipped.length && !loading && (
            <div className="empty">
              {productFilter !== 'ALL'
                ? `No shipped ${productFilter} items yet.`
                : 'No shipped items yet.'}
            </div>
          )}
        </div>
      </section>

      {selectedFeature && (
        <div className="drawer" role="dialog" aria-modal="true">
          <div className="drawer-backdrop" onClick={closeFeature} />
          <div className="drawer-panel">
            <div className="drawer-header">
              <div>
                <p className="label">{tenantConfig?.name ?? 'Product'} roadmap</p>
                <h3>{selectedFeature.title}</h3>
                <p className="meta">{selectedFeature.description}</p>
              </div>
              <button className="btn btn-ghost" onClick={closeFeature}>
                Close
              </button>
            </div>
            <div className="drawer-tags">
              {selectedFeature.product && <span className="tag">{selectedFeature.product}</span>}
              {selectedFeature.category && <span className="tag">{selectedFeature.category}</span>}
              <span className="badge" data-status={selectedFeature.status}>
                {formatStatus(selectedFeature.status)}
              </span>
            </div>
            <div className="drawer-grid">
              <div>
                <h4>Problem</h4>
                <p className="meta">{selectedFeature.problem ?? 'Share context so we can prioritize accurately.'}</p>
              </div>
              <div>
                <h4>Impact</h4>
                <p className="meta">
                  {selectedFeature.totalCredits} credits from {selectedFeature.uniqueVoters} voters signal the demand.
                </p>
              </div>
            </div>
            <div className="drawer-actions">
              <div className="vote-actions">
                {[1, 2, 3].map((amount) => (
                  <button
                    key={amount}
                    className="btn btn-mini"
                    disabled={!token && !guestVerified}
                    onClick={() => castVote(selectedFeature.id, amount)}
                  >
                    +{amount}
                  </button>
                ))}
              </div>
              <button className="btn btn-ghost" onClick={copyShareLink}>
                Copy share link
              </button>
              {shareStatus && <span className="meta">{shareStatus}</span>}
            </div>
            {token && (
              <div className="admin-panel">
                <h4>Admin actions</h4>
                <p className="meta">Requires the feedback.admin role.</p>
                <label className="token-input">
                  <span>Release notes URL</span>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={shipReleaseNotesUrl}
                    onChange={(event) => setShipReleaseNotesUrl(event.target.value)}
                  />
                </label>
                <label className="token-input">
                  <span>Release version</span>
                  <input
                    type="text"
                    placeholder="v1.2.0"
                    value={shipVersion}
                    onChange={(event) => setShipVersion(event.target.value)}
                  />
                </label>
                <button className="btn btn-primary" onClick={shipFeature}>
                  Mark as shipped
                </button>
                {adminStatus && <p className="status-note">{adminStatus}</p>}
              </div>
            )}
            {relatedFeatures.length > 0 && (
              <div className="drawer-related">
                <h4>Related requests</h4>
                <div className="related-grid">
                  {relatedFeatures.map((feature) => (
                    <button key={feature.id} className="related-card" onClick={() => openFeature(feature.id)}>
                      <span className="meta">{formatStatus(feature.status)}</span>
                      <strong>{feature.title}</strong>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
